/**
 * Launcher V2 PR C QA follow-up U7 (item 14, R8) — the Sequences step's
 * "which rung is selected right now" derivation, extracted to a pure
 * function so it's testable without a DOM (`bun:test` has no DOM under
 * `mini-apps/`, same reason as `sequence-readiness.ts`/`rung-counts.ts`).
 *
 * ── THE BUG ───────────────────────────────────────────────────────────────
 * `SequencesStep.tsx` used to derive `selectedRung` as a bare
 * `railRungs.find((r) => r.draftKey === selectedDraftKey) ?? null` — on a
 * fresh load `selectedDraftKey` is `null`, so `selectedRung` was always
 * `null` even when rungs existed, and the Sequences step showed a dead
 * "Select a segmentation to edit its sequence" empty state that no click
 * ever needed to produce (nothing on the rail *sets* `selectedDraftKey` to
 * `null` — only the initial state does).
 *
 * ── THE FIX (ported from the prototype, `playbooks-prototype-v6.html:3393`) ──
 * `selectedRung()`'s fallback chain: prefer the rung matching the current
 * explicit selection; else prefer the first MAPPED rung (a rep opening the
 * step for the first time almost certainly wants to see a rung that already
 * has a sequence attached); else just the first rung in the rail. Only when
 * the rail itself is empty does the result fall through to `null` — and
 * `null` is the ONLY case the empty-state message should render for
 * (prototype `renderStep2()`, `:6300-6302`).
 */

import type { DraftRung } from './playbook-draft';

/**
 * @param railRungs the staged ladder, in rail order (`SequencesStep`'s
 *   `railRungs`).
 * @param selectedDraftKey the rep's explicit selection, or `null` if nothing
 *   has been explicitly clicked yet this mount (or the previous selection no
 *   longer exists in `railRungs`).
 * @returns the rung to render in the editor pane, or `null` only when
 *   `railRungs` is empty.
 */
export function selectRung(
  railRungs: readonly DraftRung[],
  selectedDraftKey: string | null,
): DraftRung | null {
  return (
    railRungs.find((r) => r.draftKey === selectedDraftKey) ??
    railRungs.find((r) => r.mapping) ??
    railRungs[0] ??
    null
  );
}
